import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface CashflowProjectionEntry {
  id: string;
  user_id: string;
  entry_type: 'Operating' | 'Investment' | 'Funding' | 'Other';
  direction: 'In' | 'Out';
  label: string;
  amount: number;
  date: string;
  tags: Record<string, any>;
  scenario_id?: string;
  created_at: string;
  updated_at: string;
}

export function useInvestmentCashflow(scenarioId?: string) {
  const [entries, setEntries] = useState<CashflowProjectionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { effectiveUserId } = useAuth();

  const loadCashflowEntries = async () => {
    if (!effectiveUserId) return;

    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('cashflow_projection_entries')
        .select('*')
        .eq('user_id', effectiveUserId)
        .in('entry_type', ['Investment', 'Funding'])
        .order('date', { ascending: true });

      if (scenarioId) {
        query = query.eq('scenario_id', scenarioId);
      } else {
        query = query.is('scenario_id', null);
      }

      const { data, error: queryError } = await query;

      if (queryError) throw queryError;

      setEntries(data || []);
    } catch (err: any) {
      console.error('Error loading cashflow entries:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCashflowEntries();
  }, [effectiveUserId, scenarioId]);

  const syncInvestmentDisbursements = async () => {
    if (!effectiveUserId) throw new Error('User not authenticated');

    const { data: disbursements, error: disbError } = await supabase
      .from('investment_disbursements')
      .select(`
        *,
        investments:investment_id (name)
      `)
      .eq('user_id', effectiveUserId);

    if (disbError) throw disbError;

    for (const disb of disbursements || []) {
      const tags = {
        module: 'investments',
        investment_id: disb.investment_id,
        disbursement_id: disb.id
      };

      const label = `[INV] ${(disb as any).investments?.name || 'Investissement'} — ${disb.label}`;

      const { data: existing } = await supabase
        .from('cashflow_projection_entries')
        .select('id')
        .eq('user_id', effectiveUserId)
        .eq('tags->>disbursement_id', disb.id)
        .is('scenario_id', null)
        .maybeSingle();

      if (existing) {
        await supabase
          .from('cashflow_projection_entries')
          .update({
            label,
            amount: disb.amount,
            date: disb.due_date,
            tags,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);
      } else {
        await supabase
          .from('cashflow_projection_entries')
          .insert([{
            user_id: effectiveUserId,
            entry_type: 'Investment',
            direction: 'Out',
            label,
            amount: disb.amount,
            date: disb.due_date,
            tags,
            scenario_id: null
          }]);
      }
    }

    await loadCashflowEntries();
  };

  const syncFundingReceipts = async (scenarioDelayDays: number = 0) => {
    if (!effectiveUserId) throw new Error('User not authenticated');

    const { data: receipts, error: receiptError } = await supabase
      .from('funding_receipts')
      .select(`
        *,
        fundings:funding_id (name, default_delay_days)
      `)
      .eq('user_id', effectiveUserId);

    if (receiptError) throw receiptError;

    for (const receipt of receipts || []) {
      if (receipt.is_locked && scenarioDelayDays > 0) {
        continue;
      }

      const funding = (receipt as any).fundings;
      const delayToApply = scenarioDelayDays || receipt.simulated_delay_days || funding?.default_delay_days || 0;

      const receiptDate = new Date(receipt.expected_date);
      receiptDate.setDate(receiptDate.getDate() + delayToApply);
      const adjustedDate = receiptDate.toISOString().split('T')[0];

      const tags = {
        module: 'investments',
        funding_id: receipt.funding_id,
        receipt_id: receipt.id
      };

      const label = `[FIN] ${funding?.name || 'Financement'} — ${receipt.label}`;

      const { data: existing } = await supabase
        .from('cashflow_projection_entries')
        .select('id')
        .eq('user_id', effectiveUserId)
        .eq('tags->>receipt_id', receipt.id)
        .is('scenario_id', null)
        .maybeSingle();

      if (existing) {
        await supabase
          .from('cashflow_projection_entries')
          .update({
            label,
            amount: receipt.amount,
            date: adjustedDate,
            tags,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);
      } else {
        await supabase
          .from('cashflow_projection_entries')
          .insert([{
            user_id: effectiveUserId,
            entry_type: 'Funding',
            direction: 'In',
            label,
            amount: receipt.amount,
            date: adjustedDate,
            tags,
            scenario_id: null
          }]);
      }
    }

    await loadCashflowEntries();
  };

  const syncAll = async (scenarioDelayDays: number = 0) => {
    await syncInvestmentDisbursements();
    await syncFundingReceipts(scenarioDelayDays);
  };

  const deleteOrphanedEntries = async () => {
    if (!effectiveUserId) throw new Error('User not authenticated');

    const { data: allEntries } = await supabase
      .from('cashflow_projection_entries')
      .select('id, tags')
      .eq('user_id', effectiveUserId)
      .in('entry_type', ['Investment', 'Funding']);

    for (const entry of allEntries || []) {
      const tags = entry.tags as any;
      if (tags.disbursement_id) {
        const { data: disb } = await supabase
          .from('investment_disbursements')
          .select('id')
          .eq('id', tags.disbursement_id)
          .maybeSingle();

        if (!disb) {
          await supabase
            .from('cashflow_projection_entries')
            .delete()
            .eq('id', entry.id);
        }
      }

      if (tags.receipt_id) {
        const { data: receipt } = await supabase
          .from('funding_receipts')
          .select('id')
          .eq('id', tags.receipt_id)
          .maybeSingle();

        if (!receipt) {
          await supabase
            .from('cashflow_projection_entries')
            .delete()
            .eq('id', entry.id);
        }
      }
    }

    await loadCashflowEntries();
  };

  return {
    entries,
    loading,
    error,
    refresh: loadCashflowEntries,
    syncInvestmentDisbursements,
    syncFundingReceipts,
    syncAll,
    deleteOrphanedEntries
  };
}

export function useInvestmentStats() {
  const [stats, setStats] = useState({
    totalInvestments: 0,
    totalFundings: 0,
    remainingToFund: 0,
    unfundedCount: 0
  });
  const [loading, setLoading] = useState(true);
  const { effectiveUserId } = useAuth();

  const loadStats = async () => {
    if (!effectiveUserId) return;

    try {
      setLoading(true);

      const [investmentsResult, fundingsResult, allocationsResult] = await Promise.all([
        supabase
          .from('investments')
          .select('total_budget, id')
          .eq('user_id', effectiveUserId)
          .neq('status', 'Cancelled'),
        supabase
          .from('fundings')
          .select('total_expected')
          .eq('user_id', effectiveUserId)
          .neq('status', 'Rejected'),
        supabase
          .from('funding_allocations')
          .select('investment_id, allocated_amount')
          .eq('user_id', effectiveUserId)
      ]);

      const totalInv = (investmentsResult.data || []).reduce((sum, inv) => sum + parseFloat(inv.total_budget), 0);
      const totalFund = (fundingsResult.data || []).reduce((sum, fund) => sum + parseFloat(fund.total_expected), 0);

      const fundedByInv = (allocationsResult.data || []).reduce((acc, alloc) => {
        if (!acc[alloc.investment_id]) acc[alloc.investment_id] = 0;
        acc[alloc.investment_id] += parseFloat(alloc.allocated_amount);
        return acc;
      }, {} as Record<string, number>);

      const investments = investmentsResult.data || [];
      let totalRemaining = 0;
      let unfunded = 0;

      investments.forEach(inv => {
        const funded = fundedByInv[inv.id] || 0;
        const remaining = parseFloat(inv.total_budget) - funded;
        if (remaining > 0) {
          totalRemaining += remaining;
          if (funded === 0) unfunded++;
        }
      });

      setStats({
        totalInvestments: totalInv,
        totalFundings: totalFund,
        remainingToFund: totalRemaining,
        unfundedCount: unfunded
      });
    } catch (err) {
      console.error('Error loading investment stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, [effectiveUserId]);

  return { stats, loading, refresh: loadStats };
}
